import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, ArrowUp, Loader2, Mic, MicOff } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { streamAskSuperAnalyst, type AskMessage, type AskSource } from "@/lib/askSuperAnalyst";
import { toast } from "@/hooks/use-toast";
import { useVoiceTranscribe } from "@/hooks/useVoiceTranscribe";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  autoStartVoice?: boolean;
}

// The data centre's look (Terminal white, 26 Oct 2026 colour ruling): ink text, cool greys, SN orange only on Send.
const INK = "#0B1220";
const ORANGE = "#EE5A24";

/** The company a page is about, from /data/companies/<ticker>. */
const pageTicker = (path: string) => path.match(/^\/data\/companies\/([a-z0-9-]+)/i)?.[1]?.toUpperCase().replace(/-/g, ".") ?? null;

const DATASET_EXAMPLES: Record<string, string[]> = {
  events: ["Which companies had leadership changes this week?", "Which companies reported results this week?", "Material agreements filed this week", "Apple's 8-K filings this year"],
  "insider-trades": ["Biggest insider purchases this week", "Largest insider sales this week", "Who at NVIDIA sold shares recently?", "What fields does this dataset have?"],
  "planned-insider-sales": ["Largest planned insider sales this week", "Planned sales by Apple insiders", "What does a Form 144 notice tell me?", "What fields does this dataset have?"],
  "large-holders": ["New 13D and 13G filings this week", "Who holds more than 5% of Apple?", "What is the difference between a 13D and a 13G?", "What fields does this dataset have?"],
  "congress-trades": ["Which stocks did House members trade this week?", "Have members of Congress traded NVIDIA recently?", "House trades in Microsoft this year", "What fields does this dataset have?"],
  "short-interest": ["Netflix short interest", "Tesla days to cover", "What does days to cover mean?", "What fields does this dataset have?"],
  "financial-statements": ["Apple's revenue for the last five years", "Which S&P 500 companies have the highest return on equity?", "Microsoft free cash flow, last 10 years", "Which companies reported results this week?"],
  screener: ["Which S&P 500 companies have the highest return on equity?", "Fastest revenue growth in the S&P MidCap 400", "Highest dividend yield in the S&P 500", "Lowest debt to equity among banks"],
  licence: ["Can I use the data at work?", "How do I credit the data?", "Can an AI tool read and quote the data?", "How do I ask about a licence for my team?"],
};
const HOME_EXAMPLES = ["Apple's revenue for the last five years", "Biggest insider purchases this week", "JPMorgan's debt to equity", "Which companies had leadership changes this week?"];

/** Example questions that fit the page the reader is on. */
const examplesFor = (path: string): string[] => {
  const t = pageTicker(path);
  if (t) return [`${t} revenue and net income, last five years`, `Who has been buying or selling ${t} shares?`, `${t} debt to equity and return on equity`, `What did ${t} say with its latest results?`];
  const slug = path.match(/^\/data\/([a-z-]+)$/)?.[1];
  return (slug && DATASET_EXAMPLES[slug]) || HOME_EXAMPLES;
};

// A closing "Sources:" paragraph repeats the list drawn under the answer, so it is dropped from the text.
const tidy = (text: string) => text.replace(/\n+\s*\**sources?\**:?[^\n]*(\n[-*] [^\n]*)*\s*$/i, "").trimEnd();

const host = (url: string) => {
  try {
    const u = new URL(url);
    return u.hostname === "shikshannivesh.com" ? "" : u.hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const Sources = ({ items }: { items: AskSource[] }) => (
  <div className="mt-3 border-t border-[rgba(11,18,32,0.09)] pt-2">
    <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[#6b7686]">Sources</p>
    <ul className="mt-1 flex flex-col gap-[3px]">
      {items.map((s) => (
        <li key={s.url} className="flex items-baseline gap-2 text-[12.5px] leading-snug">
          <span className="text-[#6b7686]">→</span>
          <a
            href={s.url}
            target={s.url.startsWith("https://shikshannivesh.com/") ? undefined : "_blank"}
            rel="noopener noreferrer"
            className="text-[#0B1220] underline decoration-[rgba(11,18,32,0.25)] underline-offset-[3px] hover:decoration-current"
          >
            {s.label}
          </a>
          {host(s.url) && <span className="font-mono text-[11px] text-[#6b7686]">{host(s.url)}</span>}
        </li>
      ))}
    </ul>
  </div>
);

// Answers: ink prose, small tables with tabular figures that scroll sideways on a phone, links in ink.
const ANSWER =
  "text-[14.5px] leading-[1.6] text-[#0B1220] [&_p]:my-2 [&_p:first-child]:mt-0 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_strong]:font-semibold " +
  "[&_a]:text-[#0B1220] [&_a]:underline [&_a]:decoration-[rgba(11,18,32,0.25)] [&_a]:underline-offset-[3px] hover:[&_a]:decoration-current " +
  "[&_table]:w-full [&_table]:border-collapse [&_table]:text-[12.5px] [&_thead]:bg-[#f6f7f9] [&_th]:whitespace-nowrap [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-[11.5px] [&_th]:font-semibold [&_th]:text-[#3b4556] " +
  "[&_td]:whitespace-nowrap [&_td]:border-t [&_td]:border-[rgba(11,18,32,0.09)] [&_td]:px-2 [&_td]:py-1.5 [&_td]:tabular-nums";

export const AskSuperAnalystModal = ({ open, onOpenChange, autoStartVoice }: Props) => {
  const [messages, setMessages] = useState<AskMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const { isIndian } = useGeoCurrency();
  const { pathname } = useLocation();
  const ticker = pageTicker(pathname);
  const examples = examplesFor(pathname);

  const voice = useVoiceTranscribe({
    onTranscript: (text) => {
      setInput((prev) => (prev ? `${prev.trimEnd()} ${text}` : text));
    },
    onError: (msg) => toast({ title: "Voice input", description: msg, variant: "destructive" }),
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      if (voice.isListening) void voice.stop();
    }
  }, [open]);

  useEffect(() => {
    if (open && autoStartVoice && !voice.isListening && voice.status === "idle") {
      void voice.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStartVoice]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;
    const userMsg: AskMessage = { role: "user", content: trimmed };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setIsStreaming(true);

    let assistantSoFar = "";
    let sources: AskSource[] = [];
    const upsert = () => {
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar, sources } : m));
        }
        return [...prev, { role: "assistant", content: assistantSoFar, sources }];
      });
    };

    const controller = new AbortController();
    abortRef.current = controller;

    await streamAskSuperAnalyst({
      messages: next,
      locale: isIndian ? "india" : "international",
      page: pathname,
      onSources: (s) => {
        sources = s;
      },
      onDelta: (chunk) => {
        assistantSoFar += chunk;
        upsert();
      },
      onDone: () => setIsStreaming(false),
      onError: (err) => {
        setIsStreaming(false);
        toast({ title: "Assistant error", description: err.message, variant: "destructive" });
      },
      signal: controller.signal,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[92dvh] w-[calc(100%-16px)] max-w-[760px] flex-col gap-0 overflow-hidden rounded-[6px] border border-[rgba(11,18,32,0.16)] bg-white p-0 text-[#0B1220] shadow-[0_24px_64px_-24px_rgba(11,18,32,0.35)] sm:rounded-[6px]"
        style={{ fontFamily: '"Inter", "Geist", ui-sans-serif, system-ui, -apple-system, sans-serif' }}
      >
        <div className="flex items-center gap-2.5 border-b border-[rgba(11,18,32,0.09)] py-3 pl-4 pr-12 sm:pl-5">
          <LogoMark size={22} className="shrink-0" />
          <div className="min-w-0">
            <DialogTitle className="text-[15px] font-semibold leading-tight tracking-[-0.01em]" style={{ color: INK }}>
              SuperAnalyst
            </DialogTitle>
            <p className="truncate text-[12px] leading-tight text-[#6b7686]">Answers from US company filings</p>
          </div>
          {ticker && (
            <span className="ml-auto shrink-0 rounded-[3px] border border-[rgba(11,18,32,0.16)] px-1.5 py-[1px] font-mono text-[11px] text-[#3b4556]" title={`Questions here are about ${ticker} unless you name another company`}>
              {ticker}
            </span>
          )}
        </div>
        <DialogDescription className="sr-only">
          Ask about any company or dataset on Shikshan Nivesh Data. Each answer lists the pages and filings it comes from.
        </DialogDescription>

        <div ref={scrollRef} className="min-h-[300px] flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {messages.length === 0 ? (
            <div className="flex flex-col gap-4">
              <p className="text-[14px] leading-relaxed text-[#3b4556]">
                {ticker
                  ? `Ask about ${ticker}: its results, ratios, filings, insider trades or holders. Every figure comes with the filing it is from.`
                  : "Ask about any company we cover, or any dataset: results, ratios, 8-K events, insider trades, holders and more. Every figure comes with the filing it is from."}
              </p>
              <div>
                <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[#6b7686]">Try</p>
                <ul className="rounded-[4px] border border-[rgba(11,18,32,0.09)]">
                  {examples.map((q) => (
                    <li key={q} className="border-t border-[rgba(11,18,32,0.09)] first:border-t-0">
                      <button
                        type="button"
                        onClick={() => send(q)}
                        className="group flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-[14px] text-[#0B1220] transition-colors hover:bg-[#f6f7f9]"
                      >
                        <span>{q}</span>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-[#6b7686] transition-transform group-hover:translate-x-0.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {messages.map((m, i) =>
                m.role === "user" ? (
                  <div key={i} className="flex justify-end">
                    <div className="max-w-[85%] rounded-[4px] border border-[rgba(11,18,32,0.09)] bg-[#f6f7f9] px-3 py-2 text-[14.5px] leading-relaxed text-[#0B1220]">
                      {m.content}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="min-w-0">
                    <div className={ANSWER}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          table: ({ children }) => (
                            <div className="my-2 overflow-x-auto rounded-[3px] border border-[rgba(11,18,32,0.09)]">
                              <table>{children}</table>
                            </div>
                          ),
                          a: ({ href, children }) => (
                            <a href={href} target={href?.startsWith("https://shikshannivesh.com/") ? undefined : "_blank"} rel="noopener noreferrer">
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {tidy(m.content) || "…"}
                      </ReactMarkdown>
                    </div>
                    {!!m.sources?.length && !(isStreaming && i === messages.length - 1) && <Sources items={m.sources} />}
                  </div>
                ),
              )}
              {isStreaming && messages[messages.length - 1]?.role === "user" && (
                <div className="flex items-center gap-2 text-[13px] text-[#6b7686]">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Reading the record
                </div>
              )}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="border-t border-[rgba(11,18,32,0.09)] bg-white px-3 pb-3 pt-3 sm:px-4">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={1}
              placeholder={ticker ? `Ask about ${ticker}...` : "Ask a question..."}
              className="max-h-32 min-h-[42px] flex-1 resize-none rounded-[4px] border border-[rgba(11,18,32,0.16)] bg-white px-3 py-2.5 text-[16px] text-[#0B1220] placeholder:text-[#6b7686] focus:border-[#0B1220] focus:outline-none sm:text-[14px]"
              disabled={isStreaming}
            />
            <button
              type="button"
              onClick={voice.toggle}
              disabled={isStreaming || voice.status === "starting"}
              className={`inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[4px] border transition-colors ${
                voice.isListening
                  ? "animate-pulse border-destructive/40 bg-destructive/10 text-destructive"
                  : "border-[rgba(11,18,32,0.16)] bg-white text-[#3b4556] hover:border-[#0B1220] hover:text-[#0B1220]"
              } disabled:cursor-not-allowed disabled:opacity-40`}
              aria-label={voice.isListening ? "Stop voice input" : "Start voice input"}
              title={voice.isListening ? "Stop voice input" : "Speak your question"}
            >
              {voice.status === "starting" ? <Loader2 className="h-4 w-4 animate-spin" /> : voice.isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
            <button
              type="submit"
              disabled={!input.trim() || isStreaming}
              className="inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[4px] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ backgroundColor: ORANGE }}
              aria-label="Send"
            >
              {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
            </button>
          </div>
          {voice.isListening && (
            <p className="mt-2 px-1 text-[11px] leading-snug text-destructive">Listening{voice.partial ? `: "${voice.partial}"` : "..."}</p>
          )}
          <p className="mt-2 px-1 text-[11px] leading-snug text-[#6b7686]">
            Answers come from our data files and can still contain mistakes. Each figure links to its filing.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
